import { useTranslation } from 'react-i18next'
import { validatePassword } from '@/lib/utils/passwordValidation'
import { validateEmail } from '@/lib/utils/emailValidation'
import { validateUsername } from '@/app/validation/usernameRules'

export interface RegisterFormData {
    username: string
    email: string
    password: string
    confirmPassword: string
    userLang: string
    targetLang: string
    inviteKey: string
}

export interface RegisterValidationResult {
    isValid: boolean
    error: string | null
    /** Optional name of the field that triggered the failure; lets callers
     * surface the error inline on the field instead of (only) at page level. */
    field?: keyof RegisterFormData
}

export const useRegisterValidation = () => {
    const { t } = useTranslation()

    const validateForm = (
        formData: RegisterFormData,
        options?: { inviteKeyRequired?: boolean }
    ): RegisterValidationResult => {
        // Username validation
        if (!formData.username.trim()) {
            return { isValid: false, error: t('auth:usernameRequired') }
        }

        const usernameValidation = validateUsername(formData.username)
        if (!usernameValidation.ok) {
            if (usernameValidation.reason === 'length') {
                return { isValid: false, error: t('auth:errors.usernameLength') }
            } else {
                return { isValid: false, error: t('auth:errors.usernameChars') }
            }
        }

        // Email validation
        if (!formData.email.trim()) {
            return { isValid: false, error: t('auth:emailRequired') }
        }

        const emailValidation = validateEmail(formData.email)
        if (!emailValidation.isValid) {
            return { isValid: false, error: t(`auth:${emailValidation.errorMessage}`) }
        }

        // Password match validation
        if (formData.password !== formData.confirmPassword) {
            return { isValid: false, error: t('auth:passwordsDoNotMatch') }
        }

        // Password strength validation
        const passwordValidation = validatePassword(formData.password)
        if (!passwordValidation.isValid) {
            const failedRequirements = Object.entries(passwordValidation.requirements)
                .filter(([, req]) => !req.met)
                .map(([, req]) => t(`auth:passwordStrength.${req.label}`))
                .join(', ')
            return { isValid: false, error: t('auth:passwordRequired') + ': ' + failedRequirements }
        }

        // Invite key validation
        if (options?.inviteKeyRequired && !formData.inviteKey.trim()) {
            return {
                isValid: false,
                error: t('errors:auth.inviteKeyRequired'),
                field: 'inviteKey',
            }
        }

        return { isValid: true, error: null }
    }

    return { validateForm }
}
